import { createBrowserRouter, Navigate, type RouteObject } from 'react-router';
import { lazy, Suspense, type ReactNode } from 'react';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { RoleGuard } from '@/components/guards/RoleGuard';
import { AdminLayout } from '@/layouts/AdminLayout';
import { ShellContentFallback, ShellPageContainer } from '@/layouts/chatShell/ShellContent';
import { RedirectWithParams } from '@/components/routing/RedirectWithParams';
import { LegacyTabRedirect } from '@/components/routing/LegacyTabRedirect';
import { MOVED_TAB_IDS } from '@/components/navigation/tabIds';
import { RoleAwareRedirect } from '@/components/routing/RoleAwareRedirect';
import { ChatIdGuard, NewChatRoute } from '@/components/routing/ChatRouteGuards';
import { NotFoundPage } from '@/pages/NotFoundPage';
import { RouteErrorPage } from '@/pages/RouteErrorPage';

/**
 * Lazy-loaded route components for code splitting.
 */
const SsoCallbackPage = lazy(() =>
  import('@/pages/SsoCallbackPage').then((m) => ({
    default: m.SsoCallbackPage,
  })),
);
const TokensDemo = lazy(() =>
  import('@/pages/dev/TokensDemo').then((m) => ({ default: m.TokensDemo })),
);

// ---------------------------------------------------------------------------
// Admin pages (lazy-loaded) -- FEAT-030 hub pages
// ---------------------------------------------------------------------------
const AdminOverviewPage = lazy(() =>
  import('@/pages/admin/AdminOverviewPage').then((m) => ({
    default: m.AdminOverviewPage,
  })),
);
const PeopleHubPage = lazy(() =>
  import('@/pages/admin/people/PeopleHubPage').then((m) => ({
    default: m.PeopleHubPage,
  })),
);
const ReportsHubPage = lazy(() =>
  import('@/pages/admin/reports/ReportsHubPage').then((m) => ({
    default: m.ReportsHubPage,
  })),
);
// --- FEAT-038 TASK-010 (import) ---
const CostsHubPage = lazy(() =>
  import('@/pages/admin/costs/CostsHubPage').then((m) => ({
    default: m.CostsHubPage,
  })),
);
// --- fim FEAT-038 TASK-010 (import) ---
// --- FEAT-039 TASK-010 (import) ---
const AnalyticsHubPage = lazy(() => import('@/pages/admin/analytics/AnalyticsHubPage'));
// --- fim FEAT-039 TASK-010 (import) ---
// --- FEAT-040 TASK-011 (import) ---
const LogsHubPage = lazy(() =>
  import('@/pages/admin/logs/LogsHubPage').then((m) => ({
    default: m.LogsHubPage,
  })),
);
const OperationsHubPage = lazy(() =>
  import('@/pages/admin/operations/OperationsHubPage').then((m) => ({
    default: m.OperationsHubPage,
  })),
);
// --- fim FEAT-040 TASK-011 (import) ---
const ComplianceHubPage = lazy(() =>
  import('@/pages/admin/compliance/ComplianceHubPage').then((m) => ({
    default: m.ComplianceHubPage,
  })),
);
const ModelsHubPage = lazy(() =>
  import('@/pages/admin/models/ModelsHubPage').then((m) => ({
    default: m.ModelsHubPage,
  })),
);
const ClassifierPage = lazy(() =>
  import('@/pages/admin/classifier/ClassifierPage').then((m) => ({
    default: m.ClassifierPage,
  })),
);
const FleetHubPage = lazy(() =>
  import('@/pages/admin/fleet/FleetHubPage').then((m) => ({
    default: m.FleetHubPage,
  })),
);
// --- FEAT-035 TASK-006 connections-routes ---
// Connections group pages (lazy). Wave-3 features (FEAT-036, FEAT-038) add their imports here.
const ApiKeysPage = lazy(() =>
  import('@/pages/admin/connections/ApiKeysPage').then((m) => ({
    default: m.ApiKeysPage,
  })),
);
const ConnectionsEndpointsPage = lazy(() =>
  import('@/pages/admin/connections/ConnectionsEndpointsPage').then((m) => ({
    default: m.ConnectionsEndpointsPage,
  })),
);
const ConnectionsProvidersHubPage = lazy(() =>
  import('@/pages/admin/connections/ConnectionsProvidersHubPage').then((m) => ({
    default: m.ConnectionsProvidersHubPage,
  })),
);
// --- FEAT-036 TASK-009 (import) ---
const QuotaPoolsPage = lazy(() =>
  import('@/pages/admin/connections/QuotaPoolsPage').then((m) => ({
    default: m.QuotaPoolsPage,
  })),
);
// --- fim FEAT-036 TASK-009 (import) ---
// --- fim FEAT-035 TASK-006 connections-routes ---

// Admin standalone pages (lazy-loaded)
const OrgSettingsPage = lazy(() =>
  import('@/pages/admin/OrgSettingsPage').then((m) => ({
    default: m.OrgSettingsPage,
  })),
);
const AuditLogPage = lazy(() =>
  import('@/pages/admin/AuditLogPage').then((m) => ({
    default: m.AuditLogPage,
  })),
);
const AdminNotFound = lazy(() =>
  import('@/pages/admin/AdminNotFound').then((m) => ({
    default: m.AdminNotFound,
  })),
);
const QuotasPage = lazy(() =>
  import('@/pages/admin/QuotasPage').then((m) => ({
    default: m.QuotasPage,
  })),
);
const AuditPage = lazy(() =>
  import('@/pages/admin/AuditPage').then((m) => ({
    default: m.AuditPage,
  })),
);
const UsageCompliancePage = lazy(() =>
  import('@/pages/admin/UsageCompliancePage').then((m) => ({
    default: m.UsageCompliancePage,
  })),
);
const ComparisonsPage = lazy(() =>
  import('@/pages/admin/ComparisonsPage').then((m) => ({
    default: m.ComparisonsPage,
  })),
);

// Auth pages
const LoginPage = lazy(() =>
  import('@/pages/auth/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const RegisterPage = lazy(() =>
  import('@/pages/auth/RegisterPage').then((m) => ({
    default: m.RegisterPage,
  })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/pages/auth/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/pages/auth/ResetPasswordPage').then((m) => ({
    default: m.ResetPasswordPage,
  })),
);
const VerifyEmailPage = lazy(() =>
  import('@/pages/auth/VerifyEmailPage').then((m) => ({
    default: m.VerifyEmailPage,
  })),
);

// Security / Settings pages
const TwoFactorSetupPage = lazy(() =>
  import('@/pages/settings/TwoFactorSetupPage').then((m) => ({
    default: m.TwoFactorSetupPage,
  })),
);
const TwoFactorDisablePage = lazy(() =>
  import('@/pages/settings/TwoFactorDisablePage').then((m) => ({
    default: m.TwoFactorDisablePage,
  })),
);
const TwoFactorVerifyPage = lazy(() =>
  import('@/pages/settings/TwoFactorVerifyPage').then((m) => ({
    default: m.TwoFactorVerifyPage,
  })),
);
const RecoveryCodesPage = lazy(() =>
  import('@/pages/settings/RecoveryCodesPage').then((m) => ({
    default: m.RecoveryCodesPage,
  })),
);
const SessionsPage = lazy(() =>
  import('@/pages/settings/SessionsPage').then((m) => ({
    default: m.SessionsPage,
  })),
);
const SettingsHubPage = lazy(() =>
  import('@/pages/settings/SettingsHubPage').then((m) => ({
    default: m.SettingsHubPage,
  })),
);
const ChangePasswordPage = lazy(() =>
  import('@/pages/settings/ChangePasswordPage').then((m) => ({
    default: m.ChangePasswordPage,
  })),
);

// Chat page
const ChatShellLayout = lazy(() =>
  import('@/layouts/ChatShellLayout').then((m) => ({ default: m.ChatShellLayout })),
);
const ChatPage = lazy(() =>
  import('@/pages/ChatPage').then((m) => ({ default: m.ChatPage })),
);

// Code / Opus Build page (FEAT-029, TASK-027)
const CodeShellPage = lazy(() =>
  import('@/pages/code/CodeShellPage').then((m) => ({
    default: m.CodeShellPage,
  })),
);

// Agent pages
const AgentListPage = lazy(() =>
  import('@/pages/agents/AgentListPage').then((m) => ({
    default: m.AgentListPage,
  })),
);
const AgentEditorPage = lazy(() =>
  import('@/pages/agents/AgentEditorPage').then((m) => ({
    default: m.AgentEditorPage,
  })),
);

// Skill pages
const SkillListPage = lazy(() =>
  import('@/pages/skills/SkillListPage').then((m) => ({
    default: m.SkillListPage,
  })),
);
const SkillEditorPage = lazy(() =>
  import('@/pages/skills/SkillEditorPage').then((m) => ({
    default: m.SkillEditorPage,
  })),
);

// Workflow pages
const WorkflowListPage = lazy(() =>
  import('@/pages/workflows/WorkflowListPage').then((m) => ({
    default: m.WorkflowListPage,
  })),
);
const WorkflowEditorPage = lazy(() =>
  import('@/pages/workflows/WorkflowEditorPage').then((m) => ({
    default: m.WorkflowEditorPage,
  })),
);
const WorkflowDetailPage = lazy(() =>
  import('@/pages/workflows/WorkflowDetailPage').then((m) => ({
    default: m.WorkflowDetailPage,
  })),
);
const WorkflowRunDetailPage = lazy(() =>
  import('@/pages/workflows/WorkflowRunDetailPage').then((m) => ({
    default: m.WorkflowRunDetailPage,
  })),
);

function SuspenseWrapper({ children }: { children: ReactNode }) {
  return (
    <Suspense
      fallback={
        <div className="flex min-h-screen items-center justify-center bg-bg-primary">
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-accent-500 border-t-transparent" />
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

// Build the route tree. Dev routes are injected via lazy() and gated by import.meta.env.DEV.
const devRoutes: RouteObject[] = import.meta.env.DEV
  ? [
      {
        path: 'dev/components',
        lazy: async () => {
          const { ComponentShowcase } = await import(
            /* @vite-ignore */ '@/pages/dev/ComponentShowcase'
          );
          return {
            element: (
              <SuspenseWrapper>
                <ComponentShowcase />
              </SuspenseWrapper>
            ),
          };
        },
      },
    ]
  : [];

const appRoutes: RouteObject[] = [
  // ---- Public auth routes (no Layout wrapper, no auth required) ----
  {
    path: '/login',
    element: (
      <SuspenseWrapper>
        <LoginPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/register',
    element: (
      <SuspenseWrapper>
        <RegisterPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/forgot-password',
    element: (
      <SuspenseWrapper>
        <ForgotPasswordPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/reset-password',
    element: (
      <SuspenseWrapper>
        <ResetPasswordPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/verify-email',
    element: (
      <SuspenseWrapper>
        <VerifyEmailPage />
      </SuspenseWrapper>
    ),
  },
  {
    path: '/auth/sso/callback',
    element: (
      <SuspenseWrapper>
        <SsoCallbackPage />
      </SuspenseWrapper>
    ),
  },

  // ---- Protected routes (require authentication) ----
  {
    element: <ProtectedRoute />,
    children: [
      // ================================================================
      // Admin routes (maintainer+ with AdminLayout) -- FEAT-030
      // ================================================================
      {
        path: 'admin',
        element: (
          <RoleGuard minimumRole="maintainer" silent>
            <AdminLayout />
          </RoleGuard>
        ),
        children: [
          // ---- Live routes (ia-map 4.2) ----

          // /admin -> Overview dashboard (index)
          {
            index: true,
            element: (
              <SuspenseWrapper>
                <AdminOverviewPage />
              </SuspenseWrapper>
            ),
          },

          // /admin/reports -> ReportsHubPage (?tab=overview|builder)
          // FEAT-038: the ids in MOVED_TAB_IDS.reports moved to /admin/costs (redirect #31)
          {
            path: 'reports',
            element: (
              <LegacyTabRedirect
                when={{ param: 'tab', values: MOVED_TAB_IDS.reports }}
                to="/admin/costs?tab=overview"
              >
                <SuspenseWrapper>
                  <ReportsHubPage />
                </SuspenseWrapper>
              </LegacyTabRedirect>
            ),
          },
          // --- FEAT-038 TASK-010 (route) ---
          // /admin/costs -> CostsHubPage (?tab=overview|pricing|budgets); pricing and budgets are admin-only
          // inside the hub (maintainer sees only overview), so the layout RoleGuard (maintainer) is enough
          {
            path: 'costs',
            element: (
              <SuspenseWrapper>
                <CostsHubPage />
              </SuspenseWrapper>
            ),
          },
          // --- fim FEAT-038 TASK-010 (route) ---
          // --- FEAT-039 TASK-010 (route) ---
          // /admin/analytics -> AnalyticsHubPage (?tab=usage|utilization|cache|compaction|providers); maintainer+,
          // restricted sections are cut by the API (meta.restricted_sections), so the layout RoleGuard is enough
          {
            path: 'analytics',
            element: (
              <SuspenseWrapper>
                <AnalyticsHubPage />
              </SuspenseWrapper>
            ),
          },
          // --- fim FEAT-039 TASK-010 (route) ---
          // --- FEAT-040 TASK-011 (route) ---
          // /admin/logs (?tab=requests|upstream|console|timeline|conversations) and
          // /admin/operations (?tab=health|runtime): admin only
          {
            path: 'logs',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <LogsHubPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          {
            path: 'operations',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <OperationsHubPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          // --- fim FEAT-040 TASK-011 (route) ---

          // /admin/compliance -> ComplianceHubPage (?tab=overview|queue|intelligence|rules)
          {
            path: 'compliance',
            element: (
              <RoleGuard minimumRole="maintainer">
                <SuspenseWrapper>
                  <ComplianceHubPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },

          // /admin/compliance/audit/* sub-routes (live, preserved)
          {
            path: 'compliance/audit',
            element: (
              <RoleGuard minimumRole="maintainer">
                <SuspenseWrapper>
                  <AuditPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          {
            path: 'compliance/audit/cohort',
            element: (
              <RoleGuard minimumRole="maintainer">
                <SuspenseWrapper>
                  <AuditPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          {
            path: 'compliance/audit/messages',
            element: (
              <RoleGuard minimumRole="maintainer">
                <SuspenseWrapper>
                  <AuditPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },

          // /admin/usage-compliance (org overview + drill-down)
          {
            path: 'usage-compliance',
            element: (
              <SuspenseWrapper>
                <UsageCompliancePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'usage-compliance/team/:team_id',
            element: (
              <SuspenseWrapper>
                <UsageCompliancePage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'usage-compliance/user/:user_id',
            element: (
              <SuspenseWrapper>
                <UsageCompliancePage />
              </SuspenseWrapper>
            ),
          },

          // /admin/comparisons -> redirect to /temporal
          {
            path: 'comparisons',
            element: <Navigate to="temporal" replace />,
          },
          {
            path: 'comparisons/temporal',
            element: (
              <SuspenseWrapper>
                <ComparisonsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'comparisons/entities',
            element: (
              <SuspenseWrapper>
                <ComparisonsPage />
              </SuspenseWrapper>
            ),
          },

          // /admin/people -> PeopleHubPage (?tab=users|teams|invitations)
          {
            path: 'people',
            element: (
              <SuspenseWrapper>
                <PeopleHubPage />
              </SuspenseWrapper>
            ),
          },

          // /admin/models -> ModelsHubPage (?tab=catalog|access)
          // FEAT-035: the tab ids in MOVED_TAB_IDS.models moved to
          // /admin/connections/providers (redirect R1)
          {
            path: 'models',
            element: (
              <LegacyTabRedirect
                when={{ param: 'tab', values: MOVED_TAB_IDS.models }}
                to="/admin/connections/providers?tab=providers"
              >
                <SuspenseWrapper>
                  <ModelsHubPage />
                </SuspenseWrapper>
              </LegacyTabRedirect>
            ),
          },

          // /admin/quotas (maintainer, inherited)
          {
            path: 'quotas',
            element: (
              <SuspenseWrapper>
                <QuotasPage />
              </SuspenseWrapper>
            ),
          },

          // /admin/settings (admin-only, RoleGuard explicit)
          {
            path: 'settings',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <OrgSettingsPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },

          // /admin/classifier (admin-only, RoleGuard explicit)
          {
            path: 'classifier',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <ClassifierPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },

          // /admin/audit-log (admin-only, RoleGuard explicit)
          {
            path: 'audit-log',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <AuditLogPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },

          // /admin/fleet (admin-only, RoleGuard explicit)
          {
            path: 'fleet',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <FleetHubPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },

          // --- FEAT-035 TASK-006 connections-routes ---
          // Connections group (owner: FEAT-035, D16). All admin-only with explicit RoleGuard
          // (maintainer gets the inline 403). Wave-3 features write INSIDE this anchor only.

          // /admin/connections -> /admin/connections/endpoints
          {
            path: 'connections',
            element: (
              <RoleGuard minimumRole="admin">
                <Navigate to="/admin/connections/endpoints" replace />
              </RoleGuard>
            ),
          },
          // /admin/connections/endpoints -> ConnectionsEndpointsPage (?tab=catalog|how-to-connect|test)
          {
            path: 'connections/endpoints',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <ConnectionsEndpointsPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          // /admin/connections/api-keys -> ApiKeysPage (?tab=keys|policies) -- FEAT-033 (admin-only)
          // Fallback if that page is ever removed: <ConnectionsComingSoonPage kind="api-keys" />.
          {
            path: 'connections/api-keys',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <ApiKeysPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          // /admin/connections/providers -> ConnectionsProvidersHubPage (?tab=providers|resilience|events)
          {
            path: 'connections/providers',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <ConnectionsProvidersHubPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          // /admin/connections/quota-pools -> placeholder; FEAT-036 (TASK-009) replaces the slot content
          // --- FEAT-035 TASK-006 slot-quota-pools ---
          // --- FEAT-036 TASK-009 (route) ---
          {
            path: 'connections/quota-pools',
            element: (
              <RoleGuard minimumRole="admin">
                <SuspenseWrapper>
                  <QuotaPoolsPage />
                </SuspenseWrapper>
              </RoleGuard>
            ),
          },
          // --- fim FEAT-036 TASK-009 (route) ---
          // --- fim FEAT-035 TASK-006 slot-quota-pools ---
          // --- fim FEAT-035 TASK-006 connections-routes ---

          // ---- Redirects (ia-map 5.1, items 1-17) ----
          // #1: /admin/dashboard -> /admin
          {
            path: 'dashboard',
            element: <RedirectWithParams to="/admin" />,
          },
          // #2: /admin/users -> /admin/people?tab=users
          {
            path: 'users',
            element: <RedirectWithParams to="/admin/people?tab=users" />,
          },
          // #3: /admin/teams -> /admin/people?tab=teams
          {
            path: 'teams',
            element: <RedirectWithParams to="/admin/people?tab=teams" />,
          },
          // #4: /admin/invitations -> /admin/people?tab=invitations
          {
            path: 'invitations',
            element: <RedirectWithParams to="/admin/people?tab=invitations" />,
          },
          // #5: /admin/team-models -> /admin/models?tab=access
          {
            path: 'team-models',
            element: <RedirectWithParams to="/admin/models?tab=access" />,
          },
          // #6: /admin/model-access -> /admin/models?tab=access
          {
            path: 'model-access',
            element: <RedirectWithParams to="/admin/models?tab=access" />,
          },
          // #7: /admin/providers -> /admin/connections/providers?tab=providers (retargeted by FEAT-035 R2)
          {
            path: 'providers',
            element: <RedirectWithParams to="/admin/connections/providers?tab=providers" />,
          },
          // #8: /admin/credentials -> /admin/connections/providers?tab=providers (retargeted by FEAT-035 R3)
          {
            path: 'credentials',
            element: <RedirectWithParams to="/admin/connections/providers?tab=providers" />,
          },
          // --- FEAT-039 TASK-010 (redirect-9) ---
          // #9 retirado pela FEAT-039: /admin/analytics é o hub Analytics
          // --- fim FEAT-039 TASK-010 (redirect-9) ---
          // #10: /admin/analytics/taxonomy -> /admin/classifier?tab=taxonomy
          {
            path: 'analytics/taxonomy',
            element: <RedirectWithParams to="/admin/classifier?tab=taxonomy" />,
          },
          // #11: /admin/compliance-intelligence -> /admin/compliance?tab=intelligence
          {
            path: 'compliance-intelligence',
            element: <RedirectWithParams to="/admin/compliance?tab=intelligence" />,
          },
          // #12: /admin/compliance/classifier -> /admin/classifier
          {
            path: 'compliance/classifier',
            element: <RedirectWithParams to="/admin/classifier" />,
          },
          // #13: /admin/compliance/classifier/presets -> /admin/classifier?tab=presets
          {
            path: 'compliance/classifier/presets',
            element: <RedirectWithParams to="/admin/classifier?tab=presets" />,
          },
          // #14: /admin/compliance/classifier/test -> /admin/classifier?tab=sandbox
          {
            path: 'compliance/classifier/test',
            element: <RedirectWithParams to="/admin/classifier?tab=sandbox" />,
          },
          // #15: /admin/compliance/classifier/dashboard -> /admin/classifier?tab=dashboard
          {
            path: 'compliance/classifier/dashboard',
            element: <RedirectWithParams to="/admin/classifier?tab=dashboard" />,
          },
          // #16: /admin/workspaces -> /admin/fleet?tab=workspaces
          {
            path: 'workspaces',
            element: <RedirectWithParams to="/admin/fleet?tab=workspaces" />,
          },
          // #17: /admin/framework-health -> /admin/fleet?tab=framework
          {
            path: 'framework-health',
            element: <RedirectWithParams to="/admin/fleet?tab=framework" />,
          },
          // Defensive redirect: /admin/overview -> /admin (AreaSwitcher fix)
          {
            path: 'overview',
            element: <RedirectWithParams to="/admin" />,
          },

          // 404 fallback for unmatched /admin/* routes
          {
            path: '*',
            element: (
              <SuspenseWrapper>
                <AdminNotFound />
              </SuspenseWrapper>
            ),
          },
        ],
      },

      // ================================================================
      // Dashboard routes -- redirects (ia-map 5.1, items 18-22)
      // ================================================================
      {
        path: 'dashboard',
        element: <Layout />,
        children: [
          // #22: /dashboard -> /chat (via / existing)
          {
            index: true,
            element: <Navigate to="/" replace />,
          },
          // #18: /dashboard/analytics -> role-aware redirect
          {
            path: 'analytics',
            element: (
              <RoleAwareRedirect
                // --- FEAT-039 TASK-010 (dashboard-analytics) ---
                maintainerPath="/admin/analytics"
                // --- fim FEAT-039 TASK-010 (dashboard-analytics) ---
                memberPath="/chat"
              />
            ),
          },
          // #19: /dashboard/reports/cost -> /admin/costs?tab=overview (retargeted by FEAT-038)
          {
            path: 'reports/cost',
            element: <RedirectWithParams to="/admin/costs?tab=overview" />,
          },
          // #20: /dashboard/reports/consumption -> /admin/costs?tab=overview (retargeted by FEAT-038)
          {
            path: 'reports/consumption',
            element: <RedirectWithParams to="/admin/costs?tab=overview" />,
          },
          // #21: /dashboard/reports/builder -> /admin/reports?tab=builder
          {
            path: 'reports/builder',
            element: <RedirectWithParams to="/admin/reports?tab=builder" />,
          },
        ],
      },

      // ---- Chat shell (design.md section K): /chat* and /settings* share ONE ChatShellLayout ----
      // Sidebar, top bar and shortcuts stay mounted when moving between the Chat and the account hub.
      {
        element: (
          <SuspenseWrapper>
            <ChatShellLayout />
          </SuspenseWrapper>
        ),
        children: [
          {
            path: 'chat',
            element: (
              <Suspense fallback={<ShellContentFallback />}>
                <ChatPage />
              </Suspense>
            ),
          },
          // /chat/new creates a chat (same path as the sidebar "+" / mod+n) and replaces the URL with /chat/<id>.
          {
            path: 'chat/new',
            element: <NewChatRoute />,
          },
          // A chat id that is not a UUID goes back to /chat without reaching the chat API.
          {
            path: 'chat/:chatId',
            element: (
              <ChatIdGuard>
                <Suspense fallback={<ShellContentFallback />}>
                  <ChatPage />
                </Suspense>
              </ChatIdGuard>
            ),
          },
          {
            element: <ShellPageContainer />,
            children: [
              // /settings -> SettingsHubPage (?tab=profile|security|api-key|usage) -- FEAT-033
              {
                path: 'settings',
                element: <SettingsHubPage />,
              },
              // /settings/security -> hub security tab (fixes the "Voltar" of the security pages)
              {
                path: 'settings/security',
                element: <Navigate to="/settings?tab=security" replace />,
              },
              { path: 'settings/security/2fa/setup', element: <TwoFactorSetupPage /> },
              { path: 'settings/security/2fa/disable', element: <TwoFactorDisablePage /> },
              { path: 'settings/security/2fa/recovery-codes', element: <RecoveryCodesPage /> },
              { path: 'settings/security/sessions', element: <SessionsPage /> },
              { path: 'settings/security/password', element: <ChangePasswordPage /> },

              // Catalog pages (agents, skills, workflows) live inside the Chat shell so they get the
              // app top bar (language, theme, user menu) -- FEAT-047 TASK-023
              {
                path: 'agents',
                element: <AgentListPage />,
              },
              {
                path: 'agents/new',
                element: <AgentEditorPage />,
              },
              {
                path: 'agents/:id/edit',
                element: <AgentEditorPage />,
              },
              // Skill routes
              {
                path: 'skills',
                element: <SkillListPage />,
              },
              {
                path: 'skills/new',
                element: <SkillEditorPage />,
              },
              {
                path: 'skills/:id/edit',
                element: <SkillEditorPage />,
              },
              // Workflow routes
              {
                path: 'workflows',
                element: <WorkflowListPage />,
              },
              {
                path: 'workflows/new',
                element: <WorkflowEditorPage />,
              },
              {
                path: 'workflows/:id/edit',
                element: <WorkflowEditorPage />,
              },
              {
                path: 'workflows/:id',
                element: <WorkflowDetailPage />,
              },
              {
                path: 'workflows/:id/runs/:runId',
                element: <WorkflowRunDetailPage />,
              },
            ],
          },
        ],
      },

      // 2FA verification is a login-flow screen (centered, full page): it stays OUTSIDE the shell (design.md K.9).
      {
        path: 'settings/security/2fa/verify',
        element: (
          <SuspenseWrapper>
            <TwoFactorVerifyPage />
          </SuspenseWrapper>
        ),
      },

      // ---- Code / Opus Build routes (FEAT-029, TASK-027) ----
      {
        path: 'code',
        element: (
          <SuspenseWrapper>
            <CodeShellPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'code/new',
        element: (
          <SuspenseWrapper>
            <CodeShellPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'code/:projectId/*',
        element: (
          <SuspenseWrapper>
            <CodeShellPage />
          </SuspenseWrapper>
        ),
      },

      // ---- Main app routes (with default Layout) ----
      {
        path: '/',
        element: <Layout />,
        children: [
          {
            index: true,
            element: <Navigate to="/chat" replace />,
          },
          {
            path: 'dev/tokens',
            element: (
              <SuspenseWrapper>
                <TokensDemo />
              </SuspenseWrapper>
            ),
          },

          ...devRoutes,
        ],
      },
    ],
  },

  // ---- Unknown route (FEAT-047 TASK-023, design.md 1): full-screen 404 for anonymous and every role ----
  { path: '*', element: <NotFoundPage /> },
];

// Root pathless route carries the errorElement: 404 route responses and unexpected route errors
// render the product pages instead of the React Router developer screen.
export const router = createBrowserRouter([
  { errorElement: <RouteErrorPage />, children: appRoutes },
]);
